import React, { useState, useEffect, useRef } from "react";
import Barcode from "react-barcode";
import Typography from '@mui/material/Typography';
import { Button, Grid, Box, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip } from "@mui/material";
import { Alert } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import SelectInput from "../../components/inputFields/SelectInput_org";
import "animate.css/animate.css";
import httpRequest from "../../../HttpService";
import { useNavigate } from 'react-router-dom';
import { JSPrintManager, WSStatus, ClientPrintJob, InstalledPrinter } from 'jsprintmanager';
import PrintPanel from "../printPanel";
import CopyIcon from "@mui/icons-material/FileCopy";
import copy from "copy-to-clipboard";
import { useReactToPrint } from 'react-to-print';
import { useDropzone } from "react-dropzone";
import TextInput from "../../components/inputFields/TextInput";

const dropzoneAccept = {
  'image/jpeg': [],
  'image/png': [],
  'image/bmp': [],
  'image/gif': [],
  'application/pdf': [],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
  'audio/wav': ['.wav'],
  'video/mp4': ['.mp4'],
  'audio/mp3': ['.mp3'],
  'audio/ogg': ['.ogg'],
  'audio/m4a': ['.m4a'],
};

const FileDropzone = ({ onAccepted, onCleared, onRejected }) => {
  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    accept: dropzoneAccept,
    maxSize: 78643200,
    maxFiles: 1,
    onDrop: (accepted, rejected) => {
      if (rejected && rejected.length > 0) {
        onRejected && onRejected(rejected[0]);
        return;
      }
      if (accepted && accepted.length > 0) onAccepted(accepted);
    },
  });
  return (
    <Box
      {...getRootProps()}
      sx={{
        minHeight: 120,
        border: '1px dashed',
        borderColor: isDragActive ? 'primary.main' : 'grey.400',
        borderRadius: 1,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        cursor: 'pointer',
        backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
      }}
    >
      <input {...getInputProps()} />
      <Typography variant="body2">
        {isDragActive ? 'Drop the file here…' : 'Drag and drop a file here or click'}
      </Typography>
      {acceptedFiles.length > 0 && (
        <Typography variant="caption" sx={{ mt: 0.5 }}>
          {acceptedFiles[0].name}
        </Typography>
      )}
    </Box>
  );
};

const Finalview = (props) => {
  const navigate = useNavigate();
  const [selectPrinter, setSelectPrinter] = useState(null);
  const [selectDefaultValue, setSelectDefaultValue] = useState(-1);
  const [printsList, setPrintsList] = useState([]);
  const [isPrint, setIsPrint] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState(null);
  const [returnTrackingNumber, setReturnTrackingNumber] = useState(null);
  const [returnJobID, setReturnJobID] = useState(null);

  // Upload functionality states
  const [base64TextString, setBase64TextString] = useState(null);
  const [pictureComment, setPictureComment] = useState("");
  const [selectedFileEXT, setSelectedFileEXT] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [openSnack, setOpenSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");
  const [openUploadDialog, setOpenUploadDialog] = useState(false);

  const hasReturnTrip = !!props.info[4]?.isReturnTripEnabled && !!props.info[4]?.returnTripOption;

  const accountNumber = localStorage.getItem("current_accountNumber") !== "" ? 
    localStorage.getItem("current_accountNumber") : localStorage.getItem("accountNumber");

  const componentRef = useRef(null);

  useEffect(() => {
    props.onAlert({ target: "Initial", value: false });

    try {
      JSPrintManager.auto_reconnect = true;
      JSPrintManager.start();
      JSPrintManager.WS.onStatusChanged = () => {
        if (JSPrintManager.websocket_status === WSStatus.Open) {
          JSPrintManager.getPrintersInfo().then(async (printersList) => {
            let printers = printersList.map((item, i) => ({
              value: i,
              label: item.name,
            }));
            setPrintsList(printers);
            localStorage.setItem("PrintList", JSON.stringify(printers));

            var defaultPrinter = printers.find(obj => obj.label.includes("ZDesigner"));
            if (defaultPrinter !== undefined) {
              var index = printers.findIndex(x => x.label === defaultPrinter.label);
              setSelectPrinter(printers[index].label);
              setSelectDefaultValue(printers[index].value);
            }
          }).catch(err => console.log("JSPrintManager: Could not get printers:", err));
        }
      };
      JSPrintManager.WS.onError = (err) => {
        console.log("JSPrintManager not available - printing features disabled. Install from https://www.neodynamic.com/downloads/jspm/");
      };
    } catch (e) {
      console.log("JSPrintManager not installed - printing features will be limited:", e.message);
    }
  }, []);

  useEffect(() => {
    if (hasReturnTrip) {
      const nextJobID = props.info[5]?.postQuoteExecute_data?.nextJobID;
      if (nextJobID) {
        setReturnJobID(nextJobID);
      }
    }
  }, [hasReturnTrip, props.info[5]?.postQuoteExecute_data?.nextJobID]);

  useEffect(() => {
    const data = props.info[5]?.postQuoteExecute_data;
    const tracking = data?.nextJobTrackingNumber || data?.jobID;

    if (tracking && props.info[0]?.accountId) {
      httpRequest.getOrder(props.info[0].accountId, tracking)
        .then(response => {
          if (response.data?.TrackNumber) {
            setTrackingNumber(response.data.TrackNumber);
          }
        })
        .catch(err => console.error("Error getting main trip order:", err));
    }
  }, [props.info[5]?.postQuoteExecute_data]);

  useEffect(() => {
    if (hasReturnTrip && returnJobID && props.info[0]?.accountId) {
      httpRequest.getOrder(props.info[0].accountId, returnJobID)
        .then(response => {
          if (response.data?.TrackNumber) {
            setReturnTrackingNumber(response.data.TrackNumber);
          }
        })
        .catch(err => console.error("Error getting return trip order:", err));
    }
  }, [returnJobID, hasReturnTrip]);

  const handlePrintChange = (event) => {
    setSelectPrinter(printsList[parseInt(event.target.value)]?.label);
    setSelectDefaultValue(printsList[parseInt(event.target.value)]?.value);
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    onAfterPrint: () => setIsPrint(false),
  });

  const handleNewTrip = () => {
    window.location.reload();
  };

  // Ctrl+Enter / Cmd+Enter triggers Create New Trip when this panel is active.
  useEffect(() => {
    if (props.expanded !== 6) return;

    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        handleNewTrip();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [props.expanded]);

  const openUploadLightbox = () => {
    setOpenUploadDialog(true);
  };

  const closeUploadLightbox = () => {
    setOpenUploadDialog(false);
    setBase64TextString(null);
    setSelectedFileEXT(null);
    setPictureComment("");
  };

  // Upload handlers
  const selectFile = (files) => {
    if (files && files[0]) {
      let file = files[0];
      let fileExt = file.name.split(".").pop();
      setSelectedFileEXT(fileExt);

      if (file) {
        const reader = new FileReader();
        reader.onload = (readerEvt) => {
          let binaryString = readerEvt.target.result;
          setBase64TextString(btoa(binaryString));
        };
        reader.readAsBinaryString(file);
      }
    }
  };

  const isImage = (ext) => {
    if (ext) {
      return (ext === "jpg" || ext === "JPG" || ext === "JPEG" || ext === "jpeg" ||
              ext === "png" || ext === "PNG" || ext === "gif" || ext === "bmp" || ext === "BMP");
    }
    return false;
  };

  const doUpload = () => {
    if (!base64TextString) {
      setSnackMessage("Please select a file first");
      setSnackSeverity("warning");
      setOpenSnack(true);
      return;
    }

    if (isImage(selectedFileEXT)) {
      uploadImage();
    } else {
      uploadFile();
    }
  };

  const uploadImage = () => {
    const base64 = base64TextString;
    const BlobPath = "https://directitsqlblob.blob.core.windows.net/images/";
    const date = new Date();
    const jobID = props.info[5]?.postQuoteExecute_data?.jobID;
    const blobFileName = jobID + date.valueOf() + "." + selectedFileEXT;

    httpRequest.doUploadImage(base64, blobFileName)
      .then(() => {
        return httpRequest.doAttachment(jobID, BlobPath + blobFileName, 2, pictureComment);
      })
      .then(() => {
        setSnackMessage("Image uploaded successfully!");
        setSnackSeverity("success");
        setOpenSnack(true);
        setUploadedFiles([...uploadedFiles, { name: blobFileName, type: "image" }]);
        closeUploadLightbox();
      })
      .catch(err => {
        console.error("Error uploading image:", err);
        setSnackMessage("Failed to upload image");
        setSnackSeverity("error");
        setOpenSnack(true);
      });
  };

  const uploadFile = () => {
    const BlobPath = "https://directitsqlblob.blob.core.windows.net/order-files/";
    const jobID = props.info[5]?.postQuoteExecute_data?.jobID;
    const blobFileName = jobID + new Date().valueOf() + "." + selectedFileEXT;

    httpRequest.doUploadFile(base64TextString, blobFileName)
      .then(() => {
        return httpRequest.doAttachment(jobID, BlobPath + blobFileName, 2, pictureComment);
      })
      .then(() => {
        setSnackMessage("File uploaded successfully!");
        setSnackSeverity("success");
        setOpenSnack(true);
        setUploadedFiles([...uploadedFiles, { name: blobFileName, type: selectedFileEXT }]);
        closeUploadLightbox();
      })
      .catch(err => {
        console.error("Error uploading file:", err);
        setSnackMessage("Failed to upload file");
        setSnackSeverity("error");
        setOpenSnack(true);
      });
  };

  const commentChange = (event) => {
    setPictureComment(event.target.value);
  };

  const printOrder = () => {
    if (selectPrinter && selectPrinter.includes("ZPL")) {
      if (props.info[5]?.postQuoteExecute_data?.jobID) {
        httpRequest.getLabelString(props.info[5].postQuoteExecute_data.jobID).then(async (body) => {
          var cpj = new ClientPrintJob();
          cpj.clientPrinter = new InstalledPrinter(selectPrinter);
          cpj.printerCommands = body.data.ZPLString;
          cpj.sendToClient();
        }).catch(err => console.error("Error printing main label:", err));
      }

      if (hasReturnTrip && returnJobID) {
        httpRequest.getLabelString(returnJobID).then(async (body) => {
          var cpj = new ClientPrintJob();
          cpj.clientPrinter = new InstalledPrinter(selectPrinter);
          cpj.printerCommands = body.data.ZPLString;
          cpj.sendToClient();
        }).catch(err => console.error("Error printing return label:", err));
      }
    } else {
      setIsPrint(true);
      setTimeout(handlePrint, 100);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={hasReturnTrip ? 6 : 12}>
        <Box border="1px solid #e0e0e0" borderRadius={4} p={2} bgcolor="white">
          <Grid container direction="column" alignItems="center" spacing={1}>
            <Grid item>
              {props.info[5]?.postQuoteExecute_data?.jobID && (
                <Barcode value={props.info[5].postQuoteExecute_data.jobID} width={1.5} height={50} />
              )}
            </Grid>
            <Grid item>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                First Trip - Job ID {props.info[5]?.postQuoteExecute_data?.jobID} entered successfully!
              </Typography>
            </Grid>
            {trackingNumber && (
              <Grid item container justifyContent="center" alignItems="center" spacing={1}>
                <Grid item>
                  <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                    Tracking No: {trackingNumber}
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => copy("https://tracking.directx.ca/trackNo/" + trackingNumber)}
                  >
                    COPY URL
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>

      {hasReturnTrip && returnJobID && (
        <Grid item xs={12} md={6}>
          <Box border="1px solid #2196f3" borderRadius={4} p={2} bgcolor="#f5f8ff">
            <Grid container direction="column" alignItems="center" spacing={1}>
              <Grid item>
                <Barcode value={returnJobID} width={1.5} height={50} />
              </Grid>
              <Grid item>
                <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                  Return Trip - Job ID {returnJobID} entered successfully!
                </Typography>
              </Grid>
              {returnTrackingNumber && (
                <Grid item container justifyContent="center" alignItems="center" spacing={1}>
                  <Grid item>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                      Tracking No: {returnTrackingNumber}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<CopyIcon />}
                      onClick={() => copy("https://tracking.directx.ca/trackNo/" + returnTrackingNumber)}
                    >
                      COPY URL
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      )}

      {/* Upload Button */}
      <Grid item xs={12}>
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
          <Grid item>
            <Tooltip title="Attach Images or Documents">
              <Button
                variant="outlined"
                color="primary"
                onClick={openUploadLightbox}
                style={{ minWidth: "280px", padding: "10px", fontSize: "14px" }}
              >
                <PhotoCameraIcon style={{ marginRight: "8px" }} />
                ATTACH IMAGES
                <span style={{ margin: "0 8px" }}>or</span>
                <DescriptionIcon style={{ marginRight: "8px" }} />
                DOCUMENTS
              </Button>
            </Tooltip>
          </Grid>
          {uploadedFiles.length > 0 && (
            <Grid item xs={12}>
              <Box textAlign="center">
                <Typography variant="body2" style={{ color: "green", fontWeight: "bold" }}>
                  ✓ {uploadedFiles.length} file(s) uploaded successfully
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
          <Grid item>
            <Box display="flex" alignItems="center" bgcolor="#e8f4f8" padding="10px" borderRadius="4px">
              <Typography variant="body1" style={{ marginRight: "10px", fontWeight: "bold" }}>
                Printer
              </Typography>
              {printsList && (
                <Box minWidth={150}>
                  <SelectInput
                    optionList={printsList}
                    value={selectDefaultValue}
                    name="selectPrinter"
                    onChange={handlePrintChange}
                  />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={printOrder}
              style={{ minWidth: "160px", padding: "10px" }}
            >
              PRINT ORDER
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} container justifyContent="center">
        <Button
          variant="contained"
          color="secondary"
          onClick={handleNewTrip}
          tabIndex={-1}
          style={{ minWidth: "200px", padding: "12px", fontWeight: "bold" }}
        >
          CREATE NEW TRIP (CTRL + ENTER)
        </Button>
      </Grid>

      <div style={{ display: "none" }}>
        <div ref={componentRef}>
          {props.info[5]?.postQuoteExecute_data?.jobID && (
            <PrintPanel id={props.info[5].postQuoteExecute_data.jobID} account={props.accountNumber} />
          )}
          {hasReturnTrip && returnJobID && (
            <PrintPanel id={returnJobID} account={props.accountNumber} />
          )}
        </div>
      </div>

      <Snackbar
        open={openSnack}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={() => setOpenSnack(false)}
      >
        <Alert
          onClose={() => setOpenSnack(false)}
          severity={snackSeverity}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>

      {/* Upload Lightbox Dialog */}
      <Dialog
        open={openUploadDialog}
        onClose={closeUploadLightbox}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Attach Photos or Documents
            </Typography>
            <IconButton onClick={closeUploadLightbox} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FileDropzone
                onAccepted={selectFile}
                onRejected={(r) => {
                  const tooBig = r.errors?.some((e) => e.code === 'file-too-large');
                  setSnackMessage(tooBig ? 'File is too large' : (r.errors?.[0]?.message || 'File rejected'));
                  setSnackSeverity('error');
                  setOpenSnack(true);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                label="Comments (optional)"
                type="text"
                inputType="text"
                name="comment"
                labelSize={3}
                value={pictureComment}
                onChange={commentChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadLightbox} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={doUpload}
            color="primary"
            variant="contained"
            disabled={!base64TextString}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default Finalview;